import { useContext } from "react";
import styled from "styled-components";
import Anchor from "~/components/Anchor";
import { BlockGrid } from "~/components/Block";
import ButtonArrow from "~/components/ButtonArrow";
import ButtonOutline from "~/components/ButtonOutline";
import LinkButton from "~/components/LinkButton";
import MediaFull from "~/components/MediaFull";
import Spacer from "~/components/Spacer";
import Title from "~/components/Title";
import { useIsMobile } from "~/hooks/useIsMobile";
import { ThemeContext } from "~/lib/ThemeContext";
import { paragraph } from "~/styles/mixins";
import theme, { biggerThan, smallerThan } from "~/styles/theme";

const TextWrapper = styled.div`
  font-weight: ${(p) => p.fontWeight || "400"};
  font-family: "AIAIAI${(p) => p.fontStyle || ""}";
  color: ${(p) => p.color ?? "initial"};
  p {
    ${paragraph};
    margin: ${(p) => p.theme.sizes.vSpacing}rem 0;
    max-width: ${(p) => p.maxWidth || "none"};
  }
`;

const Img = styled.img`
  width: 100%;
  display: block;
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 1.5rem;
  align-items: center;

  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    > * {
      flex: 1;
    }
  }
`;

const ButtonContent = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ButtonSubtext = styled.span`
  font-size: 0.75em;
  font-weight: normal;
  opacity: 0.7;
  margin-top: 0.2em;
`;

export const Mobile = styled.div`
  ${(p) => biggerThan(p.theme.breakpoints.ipadPortrait)} {
    display: none;
  }
`;

export const Desktop = styled.div`
  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    display: none;
  }
`;

function ShowcaseB(props) {
  const currentTheme = useContext(ThemeContext);
  const isMobile = useIsMobile();

  const {
    titleType,
    titleLine1,
    titleLine2,
    titleLine3,
    titleSize,
    titleFontStyle,
    titleFontWeight,
    highlightColor = currentTheme.highlight,
    highlightOutline,
    color = currentTheme.foreground,
    media,
    mediaMobile,
    mediaLazy,
    text,
    fontStyle,
    fontWeight,
    anchorText,
    anchorId,
    anchorPreText,
    // First link
    buttonText,
    buttonSubtext,
    buttonType = "arrow",
    href,
    hrefAs,
    hrefTarget,
    // Second link
    buttonText2,
    buttonSubtext2,
    buttonType2 = "arrow",
    href2,
    hrefAs2,
    hrefTarget2,
    align,
    animateIn,
  } = props;

  const getButtonComponent = (type: string) => {
    switch (type) {
      case "outlined":
        return ButtonOutline;
      case "arrow":
      default:
        return ButtonArrow;
    }
  };

  const renderButtonContent = (text: string, subtext?: string) => {
    if (subtext) {
      return (
        <ButtonContent>
          <span>{text}</span>
          <ButtonSubtext>{subtext}</ButtonSubtext>
        </ButtonContent>
      );
    }
    return text;
  };

  return (
    <>
      {anchorText && (
        <Anchor id={anchorId} pre={anchorPreText} color={color}>
          {anchorText}
        </Anchor>
      )}
      <BlockGrid cols={2} oneColOn={theme.breakpoints.ipadPortrait}>
        {align === "right" && (
          <Desktop>
            {media && (
              <MediaFull
                backgroundColor={theme.colors.greyLighter}
                media={media}
                lazy={mediaLazy}
                suspended={isMobile !== false}
                href={href}
                as={hrefAs}
                target={hrefTarget}
                clickable
              />
            )}
          </Desktop>
        )}
        <div>
          <Title
            type={titleType}
            line1={titleLine1}
            line2={titleLine2}
            line3={titleLine3}
            titleSize={titleSize}
            fontStyle={titleFontStyle}
            fontWeight={titleFontWeight}
            highlightColor={highlightColor}
            highlightOutline={highlightOutline}
            color={color}
            size={0.7}
            animateIn={animateIn}
          />
          <Mobile>
            {(mediaMobile || media) && (
              <MediaFull
                backgroundColor={theme.colors.greyLighter}
                media={mediaMobile || media}
                lazy={mediaLazy}
                suspended={isMobile !== true}
                href={href}
                as={hrefAs}
                target={hrefTarget}
                clickable
              />
            )}
          </Mobile>
          {text && <TextWrapper color={color}>{text}</TextWrapper>}
          {(buttonText && href) || (buttonText2 && href2) ? (
            <>
              <Spacer size={4} backgroundColor="transparent" />
              <LinksContainer>
                {buttonText && href && (
                  <LinkButton
                    buttonComponent={getButtonComponent(buttonType)}
                    buttonProps={{ color }}
                    href={href}
                    as={hrefAs}
                    target={hrefTarget}
                  >
                    {renderButtonContent(buttonText, buttonSubtext)}
                  </LinkButton>
                )}
                {buttonText2 && href2 && (
                  <LinkButton
                    buttonComponent={getButtonComponent(buttonType2)}
                    buttonProps={{ color }}
                    href={href2}
                    as={hrefAs2}
                    target={hrefTarget2}
                  >
                    {renderButtonContent(buttonText2, buttonSubtext2)}
                  </LinkButton>
                )}
              </LinksContainer>
            </>
          ) : null}
        </div>
        {align === "left" && (
          <Desktop>
            {media && (
              <MediaFull
                backgroundColor={theme.colors.greyLighter}
                media={media}
                lazy={mediaLazy}
                suspended={isMobile !== false}
                href={href}
                as={hrefAs}
                target={hrefTarget}
                clickable
              />
            )}
          </Desktop>
        )}
      </BlockGrid>
    </>
  );
}

export default ShowcaseB;
